import { useState } from 'react';
import { Icone } from '../components/Icone';
import { BarreFiltres } from '../components/ui/BarreFiltres';
import { BarreSites } from '../components/ui/BarreSites';
import { Modale } from '../components/ui/Modale';
import { ChampFiltre, ChampRecherche } from '../components/ui/Onglets';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EcranEnErreur,
  EnteteEcran,
  MessageErreur,
  Pastille,
  couleurPastilleSite,
  type TonPastille,
} from '../components/ui/Primitives';
import { useToast } from '../components/ui/useToast';
import { ModaleInvitation } from '../components/utilisateurs/ModaleInvitation';
import { ModaleLienInvitation } from '../components/utilisateurs/ModaleLienInvitation';
import { ErreurApi } from '../lib/api';
import { horodatageReunion } from '../lib/emargement-affichage';
import { dateReunion, messageRefus } from '../lib/useFormateurs';
import { useValeurDifferee } from '../lib/useRecherche';
import {
  useDesactiverCompte,
  useReactiverCompte,
  useRenvoyerInvitation,
  useUtilisateurs,
  type EtatCompte,
  type LigneCompte,
  type ResultatInvitation,
} from '../lib/useUtilisateurs';
import { usePerimetre } from '../site/usePerimetre';

const TITRE = 'Utilisateurs';

const TONS_ETAT: Record<EtatCompte, TonPastille> = {
  ACTIF: 'success',
  INVITATION_EN_ATTENTE: 'info',
  INVITATION_EXPIREE: 'warning',
  JAMAIS_CONNECTE: 'neutral',
  DESACTIVE: 'danger',
};

/** Ce que l'état laisse savoir de plus : dernière connexion, lien envoyé, expiré. */
function detailEtat(ligne: LigneCompte): string | null {
  if (ligne.invitation && ligne.etat === 'INVITATION_EN_ATTENTE') {
    return `Envoyée le ${dateReunion(ligne.invitation.envoyeLe)} · lien valable jusqu’au ${dateReunion(ligne.invitation.expireLe)}`;
  }
  if (ligne.invitation && ligne.etat === 'INVITATION_EXPIREE') {
    return `Lien expiré le ${dateReunion(ligne.invitation.expireLe)}`;
  }
  if (ligne.dernierAcces) return `Dernière connexion le ${horodatageReunion(ligne.dernierAcces)}`;
  if (ligne.etat === 'JAMAIS_CONNECTE') return `Compte créé le ${dateReunion(ligne.creeLe)}`;
  return null;
}

/** Les états où la personne n'a pas encore son mot de passe : on lui (r)envoie le lien. */
const ETATS_A_INVITER: readonly EtatCompte[] = ['INVITATION_EN_ATTENTE', 'INVITATION_EXPIREE', 'JAMAIS_CONNECTE'];

/**
 * Geste d'une ligne. Refusé par l'API, il reste atteignable au clavier
 * (`aria-disabled`) et dit pourquoi au clic, plutôt que d'être grisé sans
 * explication.
 */
function BoutonLigne({
  libelle,
  refus,
  enCours,
  onClick,
  onRefus,
}: {
  libelle: string;
  refus: string | null;
  enCours: boolean;
  onClick: () => void;
  onRefus: (refus: string) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-disabled={refus !== null || enCours || undefined}
      title={refus ?? undefined}
      onClick={() => {
        if (enCours) return;
        if (refus) onRefus(refus);
        else onClick();
      }}
      className={`whitespace-nowrap rounded-lg px-2 py-1 text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px] ${
        refus || enCours ? 'cursor-not-allowed opacity-60' : ''
      }`}
    >
      {libelle}
    </button>
  );
}

/**
 * Comptes du personnel et des formateurs, tenus par la direction (TF-500) :
 * la liste et l'état de chaque compte, l'invitation d'un nouvel utilisateur,
 * son renvoi, la désactivation et la réactivation. Les apprenants n'y sont
 * pas : leur compte naît du lien de leur dossier.
 */
export function DirUtilisateursPage(): JSX.Element {
  const toast = useToast();
  const { site } = usePerimetre();
  const [recherche, setRecherche] = useState('');
  const [profil, setProfil] = useState('');
  const [etat, setEtat] = useState('');
  const rechercheDifferee = useValeurDifferee(recherche, 300);
  const { data, isLoading, error } = useUtilisateurs({ site, recherche: rechercheDifferee, profil, etat });

  const renvoyer = useRenvoyerInvitation();
  const desactiver = useDesactiverCompte();
  const reactiver = useReactiverCompte();

  const [invitationOuverte, setInvitationOuverte] = useState(false);
  const [lienAffiche, setLienAffiche] = useState<{ ligne: LigneCompte; lien: string } | null>(null);
  const [aDesactiver, setADesactiver] = useState<LigneCompte | null>(null);
  const [erreurGeste, setErreurGeste] = useState<string | null>(null);

  if (isLoading) return <Chargement libelle="Chargement des utilisateurs…" />;
  if (error || !data) {
    return (
      <EcranEnErreur
        titre={TITRE}
        message={error instanceof ErreurApi ? error.message : 'La liste des utilisateurs n’a pas pu être chargée.'}
      />
    );
  }

  const filtreActif = recherche !== '' || profil !== '' || etat !== '';

  const apresEnvoi = (resultat: ResultatInvitation) => {
    if (resultat.envoi.lien) setLienAffiche({ ligne: resultat.utilisateur, lien: resultat.envoi.lien });
    else toast(`Invitation envoyée à ${resultat.utilisateur.email}`);
  };

  const geste = async (action: () => Promise<void>, defaut: string) => {
    setErreurGeste(null);
    try {
      await action();
    } catch (cause) {
      setErreurGeste(messageRefus(cause, defaut));
    }
  };

  const envoyerLien = (ligne: LigneCompte) =>
    void geste(async () => apresEnvoi(await renvoyer.mutateAsync(ligne.id)), 'L’invitation n’a pas pu être renvoyée.');

  const confirmerDesactivation = (ligne: LigneCompte) =>
    void geste(async () => {
      await desactiver.mutateAsync(ligne.id);
      setADesactiver(null);
      toast(`Compte de ${ligne.prenom} ${ligne.nom} désactivé`);
    }, 'Le compte n’a pas pu être désactivé.');

  const reactiverCompte = (ligne: LigneCompte) =>
    void geste(async () => {
      await reactiver.mutateAsync(ligne.id);
      toast(`Compte de ${ligne.prenom} ${ligne.nom} réactivé`);
    }, 'Le compte n’a pas pu être réactivé.');

  const { compteurs } = data;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre={TITRE}
        sousTitre="Direction · comptes du personnel et des formateurs, invitations et accès"
        actions={
          <>
            <BarreSites />
            <BoutonPrincipal onClick={() => setInvitationOuverte(true)}>
              <Icone nom="plus" className="mr-2 h-4 w-4" />
              Inviter un utilisateur
            </BoutonPrincipal>
          </>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Compteur libelle="Comptes" valeur={compteurs.total} detail="personnel et formateurs" />
        <Compteur libelle="Actifs" valeur={compteurs.ACTIF} ton="mint" />
        <Compteur
          libelle="Invitations en attente"
          valeur={compteurs.INVITATION_EN_ATTENTE}
          detail={compteurs.INVITATION_EXPIREE > 0 ? `${compteurs.INVITATION_EXPIREE} expirée(s)` : undefined}
          ton="blue"
        />
        <Compteur libelle="Désactivés" valeur={compteurs.DESACTIVE} ton="red" />
      </div>

      {!data.courrielExpedie && (
        <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Les e-mails ne partent pas encore depuis la plateforme : après chaque invitation, le lien s’affiche une fois
          pour que vous le transmettiez vous-même.
        </p>
      )}

      <BarreFiltres
        onReinitialiser={
          filtreActif
            ? () => {
                setRecherche('');
                setProfil('');
                setEtat('');
              }
            : null
        }
      >
        <ChampRecherche
          valeur={recherche}
          onChange={setRecherche}
          etiquette="Rechercher un utilisateur"
          placeholder="Nom, prénom ou e-mail…"
        />
        <ChampFiltre valeur={profil} onChange={setProfil} etiquette="Profil">
          <option value="">Tous les profils</option>
          {data.profils.map((un) => (
            <option key={un.profil} value={un.profil}>
              {un.libelle}
            </option>
          ))}
        </ChampFiltre>
        <ChampFiltre valeur={etat} onChange={setEtat} etiquette="État du compte">
          <option value="">Tous les états</option>
          {data.etats.map((un) => (
            <option key={un.etat} value={un.etat}>
              {un.libelle}
            </option>
          ))}
        </ChampFiltre>
      </BarreFiltres>

      {erreurGeste && <MessageErreur message={erreurGeste} />}

      <Carte titre={`Comptes (${data.utilisateurs.length})`} sansBordure>
        {data.utilisateurs.length === 0 ? (
          <p className="p-4 text-sm text-codialis-gray sm:p-6">Aucun compte ne correspond à ces critères.</p>
        ) : (
          <div className="relative overflow-x-auto" role="region" aria-label="Comptes des utilisateurs" tabIndex={0}>
            <table className="w-full min-w-[680px] text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-codialis-gray">
                <tr>
                  <th className="px-4 py-3 text-left font-bold">Utilisateur</th>
                  <th className="px-4 py-3 text-left font-bold">Profil</th>
                  <th className="px-4 py-3 text-left font-bold">Antennes</th>
                  <th className="px-4 py-3 text-left font-bold">État</th>
                  <th className="px-4 py-3 text-right font-bold">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.utilisateurs.map((ligne) => {
                  const detail = detailEtat(ligne);
                  return (
                    <tr key={ligne.id} className="align-top transition hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2 font-semibold text-codialis-navy">
                          {ligne.prenom} {ligne.nom}
                          {ligne.moi && <Pastille texte="Vous" ton="mint" />}
                        </div>
                        <div className="break-all text-xs text-codialis-gray">{ligne.email}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-codialis-gray">{ligne.libelleProfil}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {ligne.sites.map((code) => (
                            <span key={code} className={`rounded px-2 py-0.5 text-xs font-bold ${couleurPastilleSite(code)}`}>
                              {code}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Pastille texte={ligne.libelleEtat} ton={TONS_ETAT[ligne.etat]} />
                        {detail && <div className="mt-1 text-[11px] text-codialis-gray">{detail}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-end gap-1">
                          {ETATS_A_INVITER.includes(ligne.etat) && (
                            <BoutonLigne
                              libelle={ligne.invitation ? 'Renvoyer l’invitation' : 'Envoyer une invitation'}
                              refus={ligne.refusRenvoi}
                              enCours={renvoyer.isPending}
                              onClick={() => envoyerLien(ligne)}
                              onRefus={setErreurGeste}
                            />
                          )}
                          {ligne.etat === 'DESACTIVE' ? (
                            <BoutonLigne
                              libelle="Réactiver"
                              refus={ligne.refusReactivation}
                              enCours={reactiver.isPending}
                              onClick={() => reactiverCompte(ligne)}
                              onRefus={setErreurGeste}
                            />
                          ) : (
                            !ligne.moi && (
                              <BoutonLigne
                                libelle="Désactiver"
                                refus={ligne.refusDesactivation}
                                enCours={desactiver.isPending}
                                onClick={() => {
                                  setErreurGeste(null);
                                  setADesactiver(ligne);
                                }}
                                onRefus={setErreurGeste}
                              />
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      <ModaleInvitation
        vue={data}
        ouverte={invitationOuverte}
        onFermer={() => setInvitationOuverte(false)}
        onInvite={(resultat) => {
          setInvitationOuverte(false);
          apresEnvoi(resultat);
        }}
      />

      {lienAffiche && (
        <ModaleLienInvitation
          destinataire={lienAffiche.ligne}
          lien={lienAffiche.lien}
          validiteJours={data.validiteInvitationJours}
          onFermer={() => setLienAffiche(null)}
        />
      )}

      <Modale
        titre="Désactiver ce compte ?"
        ouverte={aDesactiver !== null}
        onFermer={() => setADesactiver(null)}
      >
        {aDesactiver && (
          <div className="space-y-5 p-4 sm:p-6">
            <p className="text-sm text-codialis-navy">
              {aDesactiver.prenom} {aDesactiver.nom} ne pourra plus se connecter, dès maintenant. Ses dossiers, documents
              et traces restent en place, et le compte peut être réactivé.
              {aDesactiver.invitation ? ' Son lien d’invitation est annulé.' : ''}
            </p>
            {erreurGeste && <MessageErreur message={erreurGeste} />}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <BoutonSecondaire onClick={() => setADesactiver(null)}>Annuler</BoutonSecondaire>
              <BoutonPrincipal onClick={() => confirmerDesactivation(aDesactiver)} disabled={desactiver.isPending}>
                {desactiver.isPending ? 'Désactivation…' : 'Désactiver le compte'}
              </BoutonPrincipal>
            </div>
          </div>
        )}
      </Modale>
    </div>
  );
}
